/** @format */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import "../../components/ui/Header.css";
import { acceptOrganizationInvitation } from "../../api/invitations";

type Phase = "loading" | "success" | "error";

export default function AcceptInvitation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("loading");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token")?.trim();
    if (!token) {
      setPhase("error");
      setMessage("Invalid invitation link: missing token.");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await acceptOrganizationInvitation(token);
        if (cancelled) return;
        setPhase("success");
        const pwdNote = result.temporaryPasswordIssued
          ? " An account was created for you with a temporary password. Use the email invite to sign in and set a new password."
          : "";
        setMessage(
          `You have joined ${result.orgId ? "the organization" : "the workspace"} as ${result.role}.${pwdNote}`,
        );
      } catch (err: unknown) {
        if (cancelled) return;
        const anyErr = err as { response?: { data?: { error?: string } } };
        const apiMsg = anyErr?.response?.data?.error;

        if (apiMsg?.toLowerCase().includes("not pending")) {
          navigate("/", { replace: true });
          return;
        }

        setPhase("error");
        setMessage(
          apiMsg ??
            "This invitation link is invalid or has expired. Please ask the admin to send a new invite.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />
      <main className="pt-20 px-4">
        <div className="max-w-xl mx-auto">
          <div className="rounded-xl border border-slate-600/60 bg-slate-900/60 p-6 shadow-lg shadow-black/40">
            <h1 className="text-xl font-semibold mb-3">
              {phase === "success"
                ? "Invitation accepted"
                : phase === "loading"
                  ? "Accepting invitation..."
                  : "Invitation problem"}
            </h1>
            <p className="text-slate-300 mb-4 min-h-[2.5rem]">{message}</p>
            <div className="flex gap-3">
              {phase === "success" && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-blue-500/80 text-white text-sm font-medium hover:bg-blue-500"
                  onClick={() => navigate("/login")}
                >
                  Go to sign in
                </button>
              )}
              {phase === "error" && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium hover:bg-slate-600"
                  onClick={() => navigate("/")}
                >
                  Back to home
                </button>
              )}
              {phase === "loading" && (
                <span className="text-slate-400 text-sm">
                  Please wait while we process your invitation...
                </span>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

